/**
 * CORE-1654 — управление обсуждениями мастером: чистые правила, без React.
 *
 * Тикет: мастер (и админ) создаёт, переименовывает, удаляет, закрепляет и
 * раскладывает по группам обсуждения своего сообщества; участнику управляющие
 * действия не показываются вовсе. Сервер проверяет права сам — здесь только
 * решается, что рисовать и в каком порядке.
 *
 * Бэкенд (CORE-1688, swagger 13.10.2026):
 *   POST   /api/chat/masters/{id}/topics                         — завести тему
 *   PUT    /api/chat/discussions/{id}/topic                      — название/описание
 *          любого обсуждения, в том числе автосозданного для события
 *   DELETE /api/chat/discussions/{id}/topic                      — удалить обсуждение
 *          для всех; событие и публикация остаются; общий чат — 404
 *   POST/DELETE /api/chat/masters/{id}/discussions/{id}/list-pin — закреп в списке
 *   GET/POST /api/chat/masters/{id}/discussion-groups            — группы
 *   PUT/DELETE /api/chat/discussion-groups/{groupId}             — переименовать,
 *          переставить, убрать группу (обсуждения возвращаются в общий список)
 *   PUT    /api/chat/discussions/{id}/group                      — перенести
 *
 * CORE-1698 (бэкенд CORE-1710):
 *   POST/DELETE /api/chat/discussions/{id}/close                 — закрыть /
 *          снова открыть; история читается, отправка отклоняется у всех;
 *          общий чат не закрывается
 */
import type { ChatDiscussion, ChatDiscussionDetails, DiscussionGroup } from '../../types/auth';
import { isGeneral } from './communityDiscussions';

type Row = ChatDiscussion | ChatDiscussionDetails;

const TOPIC_TYPES = new Set(['topic', 'community_topic', 'manual']);

/** Ручное обсуждение («тема»), заведённое через «+», а не карточкой события. */
export function isTopicDiscussion(d: Pick<Row, 'type'> | null | undefined): boolean {
  return TOPIC_TYPES.has((d?.type ?? '').toLowerCase());
}

export interface DiscussionRowActions {
  /** «Переименовать»: название и описание. */
  canEdit: boolean;
  /** «Удалить» — для всех и насовсем. */
  canDelete: boolean;
  /** «Закрепить в списке» / «Открепить». */
  canPin: boolean;
  /** «Переместить в группу» / «Убрать из группы». */
  canMove: boolean;
  /** CORE-1698 — «Закрыть обсуждение» / «Открыть обсуждение». */
  canClose: boolean;
}

const NONE: DiscussionRowActions = { canEdit: false, canDelete: false, canPin: false, canMove: false, canClose: false };
const ALL: DiscussionRowActions = { canEdit: true, canDelete: true, canPin: true, canMove: true, canClose: true };

export function discussionRowActions(
  d: Row,
  ctx: { canManage: boolean },
): DiscussionRowActions {
  if (!ctx.canManage) return NONE;
  // Общий чат — это сам канал: у него нет названия, которое можно сменить,
  // он всегда первый в списке, его нельзя ни удалить, ни положить в группу.
  if (isGeneral(d, d.id)) return NONE;
  // Автосозданное обсуждение события управляется на тех же правах, что и
  // ручное (тикет, «Права доступа»; бэкенд — CORE-1688).
  return ALL;
}

/** Текст, который видит участник по старой ссылке на удалённое обсуждение. */
export const DELETED_DISCUSSION_NOTICE = 'Обсуждение удалено и больше недоступно.';

/** CORE-1698 — вместо поля ввода в закрытом обсуждении, у всех, включая мастера. */
export const CLOSED_DISCUSSION_NOTICE = 'Обсуждение закрыто. История сообщений доступна для чтения.';

function readTime(raw?: string | null): number {
  if (!raw) return 0;
  const value = new Date(raw).getTime();
  return Number.isFinite(value) ? value : 0;
}

/**
 * Порядок списка: общий чат всегда первый — так его отдаёт и сервер; ниже
 * обсуждения, закреплённые мастером; остальные по активности. Активность —
 * последнее сообщение, а без него момент появления обсуждения: `createdAt`
 * (CORE-1688) или начало события.
 */
export function sortDiscussions(a: ChatDiscussion, b: ChatDiscussion): number {
  const aGeneral = isGeneral(a, a.id);
  const bGeneral = isGeneral(b, b.id);
  if (aGeneral !== bGeneral) return aGeneral ? -1 : 1;
  if (a.isPinned !== b.isPinned) return a.isPinned ? -1 : 1;
  return activityTime(b) - activityTime(a);
}

function activityTime(d: ChatDiscussion): number {
  return readTime(d.lastMessageAt) || readTime(d.createdAt) || readTime(d.startsAt);
}

export interface DiscussionSection {
  /** null — общий чат (первая секция) и обсуждения без группы (последняя). */
  group: DiscussionGroup | null;
  items: ChatDiscussion[];
}

/**
 * Раскладывает список по секциям в порядке показа: общий чат; группы в порядке
 * `order`, каждая со своими обсуждениями; в конце обсуждения без группы.
 * Группы идут сразу под общим чатом, а не после сотен автосозданных
 * обсуждений событий: это структура, которую мастер завёл руками, и она должна
 * быть на виду. Пустая группа остаётся видимой: мастер только что завёл её и
 * будет наполнять. Обсуждение с группой, которой клиент не знает (ещё не
 * перечитал список групп), не пропадает — лежит среди негруппированных.
 */
export function buildDiscussionSections(
  discussions: readonly ChatDiscussion[],
  groups: readonly DiscussionGroup[],
): DiscussionSection[] {
  const ordered = [...groups].sort((a, b) => a.order - b.order || a.id - b.id);
  const known = new Set(ordered.map((g) => g.id));
  const byGroup = new Map<number, ChatDiscussion[]>(ordered.map((g) => [g.id, []]));
  const general: ChatDiscussion[] = [];
  const loose: ChatDiscussion[] = [];
  for (const d of discussions) {
    const gid = d.groupId;
    if (isGeneral(d, d.id)) general.push(d);
    else if (gid != null && known.has(gid)) byGroup.get(gid)!.push(d);
    else loose.push(d);
  }
  return [
    { group: null, items: general },
    ...ordered.map((g) => ({ group: g, items: byGroup.get(g.id)!.sort(sortDiscussions) })),
    { group: null, items: loose.sort(sortDiscussions) },
  ];
}
